import React, { FC } from 'react';

import { useIntl } from 'react-intl';

import { IncentiveEvaluationMessages } from 'components/incentive-evaluation-messages/incentive-evaluation-messages';
import BlockRenderer from 'components/sanity-block-renderer';
import { Maybe } from 'generated/sanity-graphql';
import { parseIncentiveData } from 'pages/loyalty/loyalty-incentives-components/incentive-card/parse-incentive-data';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { selectors, useAppSelector } from 'state/global-state';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';

import { ItemOffer, ItemOfferHeading, StyledIncentiveEvaluationMessage } from './styled';

interface ICartItemOfferProps {
  loyaltyOfferId?: Maybe<string>;
  cmsOfferId?: Maybe<string>;
}

export const CartItemOffer: FC<ICartItemOfferProps> = ({ loyaltyOfferId, cmsOfferId }) => {
  const { formatMessage } = useIntl();
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const cmsOffersMap = getCmsOffersMapByCmsId(cmsOffers);
  const selectedOffer = cmsOffersMap[cmsOfferId || ''];
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();

  if (selectedOffer) {
    const offerFeedback = offersFeedbackMap[loyaltyOfferId || ''];
    const titleMsgId = offerFeedback ? 'offerNotApplied' : 'offerApplied';
    const incentiveData = parseIncentiveData(selectedOffer);
    const isSelectedLoyaltyOfferAvailable = checkLoyaltyOfferAvailability(selectedOffer);

    return (
      <ItemOffer>
        <ItemOfferHeading $isApplied={!offerFeedback}>
          {formatMessage({ id: titleMsgId })}
        </ItemOfferHeading>
        <h2>{incentiveData.name}</h2>
        {incentiveData.description && <BlockRenderer content={incentiveData.description} />}
        {offerFeedback && (
          <IncentiveEvaluationMessages incentiveEvaluationResults={offerFeedback} />
        )}
        {!isSelectedLoyaltyOfferAvailable && (
          <StyledIncentiveEvaluationMessage
            hideIcon
            message={formatMessage({ id: 'offerNotAvailableInRestaurantPos' })}
          />
        )}
      </ItemOffer>
    );
  }

  if (offersLoading) {
    return null;
  }

  return (
    <ItemOffer>
      <ItemOfferHeading $isApplied={false}>
        {formatMessage({ id: 'offerNotApplied' })}
      </ItemOfferHeading>
      <StyledIncentiveEvaluationMessage
        hideIcon
        message={formatMessage({ id: 'incentiveFeedbackOfferNotAvailable' })}
      />
    </ItemOffer>
  );
};
